import { styled } from '@mui/system'
import { colors } from 'components/foundation'
import React, { useRef, useState, useCallback } from 'react'
import * as PropTypes from 'prop-types'

type FloatingPanelProps = {
  $position: string
  children: React.ReactNode
  $background?: string
  $maxWidth?: string
  $minWidth?: string
  id?: string
  'data-testid'?: string
  draggable?: boolean
}

const getPositionStyle = ($position: string) => {
  const positions = {
    top: {
      top: '24px',
      left: '24px',
    },
    right: {
      top: '50%',
      right: '24px',
      transform: 'translateY(-50%)',
    },
    left: {
      top: '50%',
      left: 'calc(40% - 13px)',
      transform: 'translateY(-50%)',
    },
    bottom: {
      bottom: '15px',
      left: window.innerWidth < 800 ? 'calc(0.5%)' : 'calc(25% - 13px)',
    },
    bottomLeft: {
      bottom: '15px',
      left: '15px',
    },
    bottomRight: {
      bottom: '15px',
      right: '15px',
    },
    topRight: {
      top: '15px',
      right: '15px',
    },
    topLeft: {
      top: '15px',
      left: '15px',
    },
  }
  return positions[$position] || { bottom: '10px', left: 'calc(40% - 13px)' }
}

const getBaseTransform = ($position: string): string => {
  const style = getPositionStyle($position)
  return 'transform' in style && style.transform
    ? (style.transform as string)
    : ''
}

export default function FloatingPanel(props: FloatingPanelProps) {
  const { children, draggable: isDraggable = false, $position, ...rest } = props
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const dragOrigin = useRef<{
    clientX: number
    clientY: number
    offsetX: number
    offsetY: number
  } | null>(null)

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault()
      dragOrigin.current = {
        clientX: e.clientX,
        clientY: e.clientY,
        offsetX: dragOffset.x,
        offsetY: dragOffset.y,
      }
      ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    },
    [dragOffset],
  )

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragOrigin.current) return
    setDragOffset({
      x: dragOrigin.current.offsetX + (e.clientX - dragOrigin.current.clientX),
      y: dragOrigin.current.offsetY + (e.clientY - dragOrigin.current.clientY),
    })
  }, [])

  const onPointerUp = useCallback(() => {
    dragOrigin.current = null
  }, [])

  if (isDraggable) {
    const baseTransform = getBaseTransform($position)
    const combinedTransform = [
      baseTransform,
      `translate(${dragOffset.x}px, ${dragOffset.y}px)`,
    ]
      .filter(Boolean)
      .join(' ')
    return (
      <StyledRoot
        $position={$position}
        {...rest}
        style={{ transform: combinedTransform }}
      >
        <DragHandle
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
        />
        {React.Children.toArray(children)}
      </StyledRoot>
    )
  }

  return (
    <StyledRoot $position={$position} {...rest}>
      {React.Children.toArray(children)}
    </StyledRoot>
  )
}

FloatingPanel.propTypes = {
  $position: PropTypes.oneOf([
    'top',
    'right',
    'bottom',
    'left',
    'topRight',
    'topLeft',
    'bottomRight',
    'bottomLeft',
  ]).isRequired,
}

const FloatingPanelHeader = styled('h1')({
  color: colors.brand,
  fontSize: 'clamp(10px, calc((100vw + 100vh)/2 * 0.01), 10px)',
  maxFontSize: '10px',
  textAlign: 'center',
  margin: 0,
  marginBottom: '8px',
  padding: 0,
})

FloatingPanel.Header = FloatingPanelHeader

const DragHandle = styled('div')({
  width: '100%',
  height: '12px',
  cursor: 'move',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '4px',
  '&::before': {
    content: '""',
    display: 'block',
    width: '32px',
    height: '4px',
    borderRadius: '2px',
    backgroundColor: colors.greyLight,
  },
})

const StyledRoot = styled('div')<FloatingPanelProps>(({
  $position,
  $background,
  $maxWidth,
  $minWidth,
}) => {
  return {
    display: 'block',
    position: 'absolute',
    zIndex: 1,
    padding: '5px',
    borderRadius: '4px',
    maxWidth: $maxWidth || undefined,
    minWidth: $minWidth || undefined,
    wordBreak: 'break-word',
    ...getPositionStyle($position),
    ...($background === 'white' && {
      background: 'white',
    }),
  }
})
